/* eslint-disable no-alert */
import { DragEvent, FC, useEffect, useRef, useState } from 'react';
import { Buffer } from 'buffer';
import Uppie from 'uppie';

import { MAX_UPLOAD_SIZE_MB, MAX_UPLOAD_SIZE_MB_IMAGE_FALLBACK, MimeType } from '@constants';
import { IMediaMetadata } from '@interface/media';
import { getImageMetadata, getVideoMetadata } from '@lib/media/common';
import { validateCumulativeFileSize } from '@utils/file';
import { getMimeType } from '@utils/mime-type';
import { hasViewBox } from '@utils/svg';
import { prepareWebDirectory } from '@utils/web';

import * as Styles from './styles';

export interface IUploadedAsset {
  name: string;
  mimeType: string;
  asset: File | Record<string, File>;
  buffer?: Buffer;
  uri?: string;
  blobUrl?: string;
  dimensions?: IMediaMetadata;
}

export interface IUpload {
  onChange: (props: IUploadedAsset) => void;
  restrict?: string[];
  directories?: boolean;
  useFallbackImageMaxSize?: boolean;
}

export const Upload: FC<IUpload> = ({
  onChange = () => null,
  restrict,
  directories = false,
  useFallbackImageMaxSize = false,
}) => {
  const [hover, setHover] = useState(false);
  const [message, setMessage] = useState(`Choose a ${directories ? 'directory' : 'file'} or drag it here`);
  const [originalAsset, setOriginalAsset] = useState<File | Record<string, File> | null>();

  const uploadWrapper = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleAsDirectory = async (asset: Record<string, File>) => {
      setMessage('web directory');

      const webDirectory = await prepareWebDirectory(asset);

      onChange({ name: '', mimeType: MimeType.WEB, asset: webDirectory });
    };

    const handleAsFile = async (asset: File) => {
      setMessage(asset.name);
      const buffer = Buffer.from(await asset.arrayBuffer());

      // set reader for preview
      const reader = new FileReader();
      reader.addEventListener('load', async (ev) => {
        let uri =
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
        let blobUrl;
        if (ev.target) {
          uri = ev.target.result as string;
          blobUrl = URL.createObjectURL(asset);
        }

        // mimeType
        const mimeType = asset.type !== '' ? asset.type : ((await getMimeType(asset)) as string);

        // get width / height
        let dimensions;
        switch (mimeType) {
          case MimeType.PNG:
          case MimeType.JPEG:
          case MimeType.GIF:
            dimensions = await getImageMetadata(asset);
            break;
          case MimeType.MP4:
            dimensions = await getVideoMetadata(asset);
            break;
          case MimeType.PDF:
            dimensions = { width: 595, height: 842 };
            break;
          default:
            dimensions = { width: 100, height: 100 };
        }

        onChange({ name: asset.name, mimeType, asset, dimensions, buffer, uri, blobUrl });
      });
      reader.readAsDataURL(asset);
    };

    const onUploadAsset = async (asset: File | Record<string, File>) => {
      setOriginalAsset(asset);

      if (asset instanceof File) {
        handleAsFile(asset);
      } else {
        handleAsDirectory(asset);
      }
    };

    const handleError = (error: string) => {
      // TODO: Implement proper error modal
      alert(error);
    };

    const uploadHandler = async (_event: Event, formData: FormData) => {
      if (formData === undefined) return;

      const fileList = Array.from(formData.values()).map((entry) => entry as File);

      if (directories) {
        if (!validateCumulativeFileSize(fileList, MAX_UPLOAD_SIZE_MB)) {
          handleError(`Directory exceeds ${MAX_UPLOAD_SIZE_MB}MB limit`);
          return;
        }

        const directory: Record<string, File> = {};
        fileList.forEach((file) => {
          directory[file.name] = file;
        });
        onUploadAsset(directory);
      } else {
        const file = fileList[0];

        // check file size

        // If image and no ffmpeg support, fall back to Cloudinary image file size limit
        if (useFallbackImageMaxSize && file.type.startsWith('image')) {
          if (!validateCumulativeFileSize([file], MAX_UPLOAD_SIZE_MB_IMAGE_FALLBACK)) {
            handleError(
              `Your browser doesn't support some minting features, making the maximum image file size ${MAX_UPLOAD_SIZE_MB_IMAGE_FALLBACK} MB instead of ${MAX_UPLOAD_SIZE_MB} MB. Please try a different browser, or consider reducing the file size.`
            );
            return;
          }
        } else if (!validateCumulativeFileSize([file], MAX_UPLOAD_SIZE_MB)) {
          handleError(`File exceeds ${MAX_UPLOAD_SIZE_MB}MB limit.`);
          return;
        }

        // check SVG has viewBox
        if (file.type === MimeType.SVG) {
          const check = await hasViewBox(file);
          if (!check) {
            handleError('SVG file is missing required viewBox attribute.');
            return;
          }
        }

        onUploadAsset(fileList[0]);
      }
    };

    if (uploadWrapper.current === null) {
      return;
    }

    const uppie = new Uppie();
    uppie(uploadWrapper.current, uploadHandler);
    uppie(uploadWrapper.current.querySelector('input[type="file"]'), uploadHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    setMessage('Drop here to upload');
    setHover(true);
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (!originalAsset) {
      const entity = directories ? 'directory' : 'file';
      setMessage(`Choose a ${entity} or drag it here`);
    } else {
      const name = directories ? 'web directory' : (originalAsset.name as string);
      setMessage(name);
    }

    setHover(false);
  };

  const extra: Record<string, string | boolean> = {
    hidden: true,
  };

  const extraDir: Record<string, string | boolean> = {
    directory: 'true',
    webkitdirectory: 'true',
    mozdirectory: 'true',
    msdirectory: 'true',
    odirectory: 'true',
    allowdirs: 'true',
  };

  let supported: string | null = null;

  if (restrict && restrict.length) {
    extra.accept = restrict.join(',');
    const types = restrict.filter((r) => r.startsWith('.')).map((r) => r.substring(1));
    supported = `Supported: ${types.join(', ')}`;
  }

  return (
    <div ref={uploadWrapper} onDragOver={handleDragOver} onDragLeave={onDragLeave}>
      <Styles.Label hover={hover}>
        <i>upload_file</i>
        <div>{message}</div>
        {!originalAsset && !!supported && <Styles.Subtext>{supported}</Styles.Subtext>}
        {directories ? (
          <Styles.Input id="upload" type="file" {...extra} {...extraDir} />
        ) : (
          <Styles.Input id="upload" type="file" {...extra} />
        )}
      </Styles.Label>
    </div>
  );
};
